import React, { useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import Login from '../pages/Login';

interface AuthGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * AuthGuard component that protects routes requiring authentication.
 * 
 * Features:
 * - Checks authentication status on mount
 * - Displays login page if user is not authenticated
 * - Preserves intended destination URL for redirect after login
 * - Shows loading state while checking authentication
 * 
 * Usage:
 * <AuthGuard>
 *   <ProtectedComponent />
 * </AuthGuard>
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children, fallback }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    setIsChecking(true);
    
    try {
      const authenticated = await authService.isAuthenticated();
      setIsAuthenticated(authenticated);
    } catch (error) {
      console.error('Authentication check failed:', error);
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  // Show loading state while checking authentication
  if (isChecking) {
    return fallback !== undefined ? (
      <>{fallback}</>
    ) : (
      <div className="min-h-screen flex items-center justify-center bg-ie-pale">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-ie-cyan mb-4"></div>
          <h2 className="text-xl font-semibold text-ie-navy">
            Checking authentication...
          </h2>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    // Store current path for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/' && currentPath !== '/login') {
      // Add redirect parameter to current URL for deep linking
      const url = new URL(window.location.href);
      if (!url.searchParams.has('redirect')) {
        url.searchParams.set('redirect', currentPath);
        window.history.replaceState({}, '', url.toString());
      }
    }
    
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // User is authenticated, render protected content
  return <>{children}</>;
};

export default AuthGuard;
